import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { casesApi } from '../../services/cases';
import { clientsApi } from '../../services/clients';
import { timeApi, CreateTimeEntryRequest, UpdateTimeEntryRequest } from '../../services/time';
import { tasksApi, CreateTaskRequest } from '../../services/tasks';
import { documentsApi, packagesApi } from '../../services/documents';
import { invoicesApi, CreateInvoiceRequest } from '../../services/invoices';
import { Case, Client, TimeEntry, Task, ActivityType, TaskPriority, Document, DocumentCategory, Invoice, InvoiceStatus, DocumentV2, DocumentProcessingStatus, DocumentCategoryV2, DocumentPackage } from '../../types';
import { formatLocalDate } from '../../utils/date';
import { DocumentUploadModal } from '../../components/DocumentUploadModal';
import { PackageList, PackageDetail, CreatePackageModal } from '../../components/PackageManagement';

const ACTIVITY_TYPES: { value: ActivityType; label: string }[] = [
  { value: 'meeting', label: 'Meeting' },
  { value: 'phone-call', label: 'Phone Call' },
  { value: 'email', label: 'Email' },
  { value: 'document-review', label: 'Document Review' },
  { value: 'research', label: 'Research' },
  { value: 'travel', label: 'Travel' },
  { value: 'court', label: 'Court' },
  { value: 'other', label: 'Other' },
];

export const CaseDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [documents, setDocuments] = useState<DocumentV2[]>([]);
  const [packages, setPackages] = useState<DocumentPackage[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'time' | 'tasks' | 'documents' | 'packages' | 'invoices'>('time');
  const [showTimeForm, setShowTimeForm] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);

  useEffect(() => {
    if (id) loadData(id);
  }, [id]);

  const loadData = async (caseId: string) => {
    try {
      setLoading(true);
      const [caseResult, timeResult, taskResult, docResult, pkgResult, invResult] = await Promise.all([
        casesApi.get(caseId),
        timeApi.list(caseId),
        tasksApi.list(caseId),
        documentsApi.list(caseId),
        packagesApi.list(caseId),
        invoicesApi.list(caseId),
      ]);
      setCaseData(caseResult);
      setTimeEntries(timeResult.timeEntries || []);
      setTasks(taskResult.tasks || []);
      setDocuments(docResult.documents || []);
      setPackages(pkgResult.packages || []);
      setInvoices(invResult.invoices || []);
      const clientResult = await clientsApi.get(caseResult.clientId);
      setClient(clientResult);
    } catch (err) {
      console.error('Failed to load case:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string, colors: Record<string, string>) => (
    <span style={{ backgroundColor: colors[status] || '#9e9e9e', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', textTransform: 'capitalize' }}>
      {status.replace('-', ' ')}
    </span>
  );

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const totalMinutes = timeEntries.reduce((sum, t) => sum + t.duration, 0);

  if (loading) return <Layout><div style={{ padding: '24px' }}>Loading...</div></Layout>;
  if (!caseData) return <Layout><div style={{ padding: '24px', color: 'red' }}>Case not found</div></Layout>;

  return (
    <Layout>
      <div style={{ padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <button onClick={() => navigate(`/clients/${caseData.clientId}`)} style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', marginBottom: '8px' }}>
              ← Back to Client
            </button>
            <h1 style={{ margin: 0 }}>{caseData.childLastName}, {caseData.childFirstName}</h1>
            <div style={{ marginTop: '8px' }}>{getStatusBadge(caseData.status, { active: '#4caf50', closed: '#9e9e9e', 'on-hold': '#ff9800' })}</div>
          </div>
          <button onClick={() => navigate(`/cases/${id}/edit`)} style={{ backgroundColor: '#1976d2', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer' }}>
            Edit Case
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
          <div style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '8px' }}>
            <h3 style={{ marginTop: 0 }}>Student Information</h3>
            <p><strong>Name:</strong> {caseData.childFirstName} {caseData.childLastName}</p>
            <p><strong>Grade:</strong> {caseData.grade || '-'}</p>
            <p><strong>School:</strong> {caseData.school || '-'}</p>
            <p><strong>District:</strong> {caseData.schoolDistrict || '-'}</p>
          </div>
          <div style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '8px' }}>
            <h3 style={{ marginTop: 0 }}>Parent/Guardian</h3>
            {client && (
              <>
                <p><strong>Name:</strong> {client.firstName} {client.lastName}</p>
                <p><strong>Email:</strong> {client.email || '-'}</p>
                <p><strong>Phone:</strong> {client.phone || '-'}</p>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '2px solid #e0e0e0' }}>
          <button onClick={() => setActiveTab('time')} style={{ padding: '12px 24px', border: 'none', backgroundColor: activeTab === 'time' ? '#1976d2' : 'transparent', color: activeTab === 'time' ? 'white' : '#666', borderRadius: '4px 4px 0 0', cursor: 'pointer', fontWeight: 500 }}>
            Time ({formatDuration(totalMinutes)})
          </button>
          <button onClick={() => setActiveTab('tasks')} style={{ padding: '12px 24px', border: 'none', backgroundColor: activeTab === 'tasks' ? '#1976d2' : 'transparent', color: activeTab === 'tasks' ? 'white' : '#666', borderRadius: '4px 4px 0 0', cursor: 'pointer', fontWeight: 500 }}>
            Tasks ({tasks.filter(t => t.status !== 'completed').length})
          </button>
          <button onClick={() => setActiveTab('documents')} style={{ padding: '12px 24px', border: 'none', backgroundColor: activeTab === 'documents' ? '#1976d2' : 'transparent', color: activeTab === 'documents' ? 'white' : '#666', borderRadius: '4px 4px 0 0', cursor: 'pointer', fontWeight: 500 }}>
            Documents ({documents.length})
          </button>
          <button onClick={() => setActiveTab('packages')} style={{ padding: '12px 24px', border: 'none', backgroundColor: activeTab === 'packages' ? '#1976d2' : 'transparent', color: activeTab === 'packages' ? 'white' : '#666', borderRadius: '4px 4px 0 0', cursor: 'pointer', fontWeight: 500 }}>
            Packages ({packages.length})
          </button>
          <button onClick={() => setActiveTab('invoices')} style={{ padding: '12px 24px', border: 'none', backgroundColor: activeTab === 'invoices' ? '#1976d2' : 'transparent', color: activeTab === 'invoices' ? 'white' : '#666', borderRadius: '4px 4px 0 0', cursor: 'pointer', fontWeight: 500 }}>
            Invoices ({invoices.length})
          </button>
        </div>

        {activeTab === 'time' && (
          <TimeSection caseId={id!} timeEntries={timeEntries} showForm={showTimeForm} setShowForm={setShowTimeForm} onRefresh={() => loadData(id!)} />
        )}

        {activeTab === 'tasks' && (
          <TaskSection caseId={id!} tasks={tasks} showForm={showTaskForm} setShowForm={setShowTaskForm} onRefresh={() => loadData(id!)} />
        )}

        {activeTab === 'documents' && (
          <DocumentSectionV2 caseId={id!} documents={documents} onRefresh={() => loadData(id!)} />
        )}

        {activeTab === 'packages' && (
          <PackageSection caseId={id!} packages={packages} onRefresh={() => loadData(id!)} />
        )}

        {activeTab === 'invoices' && (
          <InvoiceSection caseId={id!} invoices={invoices} timeEntries={timeEntries} onRefresh={() => loadData(id!)} />
        )}
      </div>
    </Layout>
  );
};

/**
 * Pure function to determine if a time entry should display the billed icon
 * This mirrors the logic in the TimeSection component
 */
export function shouldDisplayBilledIcon(entry: TimeEntry): boolean {
  return entry.billed === true;
}

/**
 * Pure function to determine the row background style for a time entry
 * This mirrors the logic in the TimeSection component
 */
export function getRowBackgroundStyle(entry: TimeEntry): string {
  return entry.billed ? '#f5f5f5' : 'transparent';
}

/**
 * Pure function to determine if a time entry row should be clickable (editable)
 * Unbilled entries are clickable, billed entries are not
 */
export function isTimeEntryClickable(entry: TimeEntry): boolean {
  return entry.billed === false;
}

/**
 * Pure function to get the cursor style for a time entry row
 * Returns 'pointer' for unbilled (clickable) entries, 'default' for billed entries
 */
export function getTimeEntryCursorStyle(entry: TimeEntry): 'pointer' | 'default' {
  return entry.billed ? 'default' : 'pointer';
}

/**
 * Handler result type for row click behavior
 */
export type RowClickResult = 
  | { action: 'open-modal'; entry: TimeEntry }
  | { action: 'none'; reason: 'billed' };

/**
 * Pure function to determine the result of clicking a time entry row
 * Returns the action to take based on the entry's billed status
 */
export function handleTimeEntryRowClick(entry: TimeEntry): RowClickResult {
  if (entry.billed) {
    return { action: 'none', reason: 'billed' };
  }
  return { action: 'open-modal', entry };
}

/**
 * Edit form data type for time entry editing
 */
export interface EditFormData {
  date: string;
  duration: number;
  activityType: ActivityType;
  description: string;
  billable: boolean;
}

/**
 * Duration validation result type
 */
export interface DurationValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Pure function to validate duration for time entry editing
 * Duration must be >= 15 minutes and in 15-minute increments
 * **Validates: Requirements 3.3, 3.4**
 */
export function validateDuration(duration: number): DurationValidationResult {
  if (duration < 15) {
    return { valid: false, error: 'Duration must be at least 15 minutes' };
  }
  if (duration % 15 !== 0) {
    return { valid: false, error: 'Duration must be in 15-minute increments' };
  }
  return { valid: true };
}

/**
 * Pure function to create edit form data from a time entry
 * This is used to pre-populate the edit modal with the entry's current values
 */
export function createEditFormFromEntry(entry: TimeEntry): EditFormData {
  return {
    date: entry.date,
    duration: entry.duration,
    activityType: entry.activityType,
    description: entry.description,
    billable: entry.billable,
  };
}

/**
 * Cancel edit state type - represents the state after canceling an edit
 */
export interface CancelEditState {
  editingEntry: TimeEntry | null;
  editForm: Partial<EditFormData>;
}

/**
 * Pure function to simulate cancel behavior
 * Returns the state that should result from canceling an edit
 * This mirrors the handleEditCancel function in the TimeSection component
 * **Validates: Requirements 3.5**
 */
export function simulateCancelEdit(): CancelEditState {
  return {
    editingEntry: null,
    editForm: {},
  };
}

/**
 * Pure function to check if original entry data is preserved after cancel
 * Compares the original entry with the entry after a cancel operation
 * The original entry should remain unchanged
 * **Validates: Requirements 3.5**
 */
export function isOriginalDataPreserved(
  originalEntry: TimeEntry,
  entryAfterCancel: TimeEntry
): boolean {
  return (
    originalEntry.timeEntryId === entryAfterCancel.timeEntryId &&
    originalEntry.date === entryAfterCancel.date &&
    originalEntry.duration === entryAfterCancel.duration &&
    originalEntry.activityType === entryAfterCancel.activityType &&
    originalEntry.description === entryAfterCancel.description &&
    originalEntry.billable === entryAfterCancel.billable &&
    originalEntry.billed === entryAfterCancel.billed
  );
}

// Time Section Component
const TimeSection: React.FC<{ caseId: string; timeEntries: TimeEntry[]; showForm: boolean; setShowForm: (v: boolean) => void; onRefresh: () => void }> = ({ caseId, timeEntries, showForm, setShowForm, onRefresh }) => {
  const [form, setForm] = useState<CreateTimeEntryRequest>({ caseId, date: new Date().toISOString().split('T')[0], duration: 15, activityType: 'meeting', description: '', billable: true });
  const [saving, setSaving] = useState(false);
  
  // State for edit modal
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [editForm, setEditForm] = useState<UpdateTimeEntryRequest>({});
  const [updating, setUpdating] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      await timeApi.create(form);
      setShowForm(false);
      setForm({ ...form, description: '' });
      onRefresh();
    } catch (err) {
      alert('Failed to save time entry');
    } finally {
      setSaving(false);
    }
  };

  const handleRowClick = (entry: TimeEntry) => {
    const result = handleTimeEntryRowClick(entry);
    if (result.action === 'open-modal') {
      setEditingEntry(result.entry);
      // Pre-populate form with current entry values using pure function
      const formData = createEditFormFromEntry(result.entry);
      setEditForm(formData);
    }
    // If action is 'none', do nothing (billed entry)
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEntry) return;
    
    // Clear any previous error
    setEditError(null);
    
    // Validate duration before submission
    if (editForm.duration !== undefined) {
      const validationResult = validateDuration(editForm.duration);
      if (!validationResult.valid) {
        setEditError(validationResult.error || 'Invalid duration');
        return;
      }
    }
    
    try {
      setUpdating(true);
      await timeApi.update(editingEntry.timeEntryId, editForm);
      setEditingEntry(null);
      setEditForm({});
      setEditError(null);
      onRefresh();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update time entry';
      setEditError(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  const handleEditCancel = () => {
    setEditingEntry(null);
    setEditForm({});
    setEditError(null);
  };

  return (
    <div>
      {/* Edit Time Entry Modal */}
      {editingEntry && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} data-testid="edit-time-entry-modal">
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '500px', width: '90%' }}>
            <h3 style={{ marginTop: 0 }}>Edit Time Entry</h3>
            {editError && (
              <div 
                style={{ 
                  backgroundColor: '#ffebee', 
                  color: '#c62828', 
                  padding: '12px', 
                  borderRadius: '4px', 
                  marginBottom: '16px',
                  fontSize: '14px'
                }}
                data-testid="edit-error-message"
              >
                {editError}
              </div>
            )}
            <form onSubmit={handleEditSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Date</label>
                  <input 
                    type="date" 
                    value={editForm.date || ''} 
                    onChange={e => setEditForm({ ...editForm, date: e.target.value })} 
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} 
                    required 
                    data-testid="edit-date-input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Duration (min)</label>
                  <select 
                    value={editForm.duration || 15} 
                    onChange={e => setEditForm({ ...editForm, duration: Number(e.target.value) })} 
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    data-testid="edit-duration-select"
                  >
                    {[15, 30, 45, 60, 90, 120, 180, 240].map(d => <option key={d} value={d}>{d} min</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Activity Type</label>
                <select 
                  value={editForm.activityType || 'meeting'} 
                  onChange={e => setEditForm({ ...editForm, activityType: e.target.value as ActivityType })} 
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  data-testid="edit-activity-select"
                >
                  {ACTIVITY_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Description</label>
                <input 
                  value={editForm.description || ''} 
                  onChange={e => setEditForm({ ...editForm, description: e.target.value })} 
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} 
                  required 
                  data-testid="edit-description-input"
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <input 
                  type="checkbox" 
                  checked={editForm.billable ?? true} 
                  onChange={e => setEditForm({ ...editForm, billable: e.target.checked })} 
                  id="edit-billable" 
                  data-testid="edit-billable-checkbox"
                />
                <label htmlFor="edit-billable">Billable</label>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  onClick={handleEditCancel} 
                  style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'white' }}
                  data-testid="edit-cancel-button"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={updating} 
                  style={{ backgroundColor: '#1976d2', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
                  data-testid="edit-save-button"
                >
                  {updating ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0 }}>Time Entries</h3>
        <button onClick={() => setShowForm(!showForm)} style={{ backgroundColor: '#4caf50', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>
          {showForm ? 'Cancel' : '+ Add Time'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Date</label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} required />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Duration (min)</label>
              <select value={form.duration} onChange={e => setForm({ ...form, duration: Number(e.target.value) })} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}>
                {[15, 30, 45, 60, 90, 120, 180, 240].map(d => <option key={d} value={d}>{d} min</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Activity</label>
              <select value={form.activityType} onChange={e => setForm({ ...form, activityType: e.target.value as ActivityType })} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}>
                {ACTIVITY_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'end' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={form.billable} onChange={e => setForm({ ...form, billable: e.target.checked })} /> Billable
              </label>
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Description</label>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What did you work on?" style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} required />
          </div>
          <button type="submit" disabled={saving} style={{ backgroundColor: '#1976d2', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>
            {saving ? 'Saving...' : 'Save Time Entry'}
          </button>
        </form>
      )}

      {timeEntries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px', color: '#666', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>No time entries yet.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ textAlign: 'left', padding: '8px', width: '30px' }}></th>
              <th style={{ textAlign: 'left', padding: '8px' }}>Date</th>
              <th style={{ textAlign: 'left', padding: '8px' }}>Activity</th>
              <th style={{ textAlign: 'left', padding: '8px' }}>Description</th>
              <th style={{ textAlign: 'right', padding: '8px' }}>Duration</th>
              <th style={{ textAlign: 'center', padding: '8px' }}>Billable</th>
            </tr>
          </thead>
          <tbody>
            {timeEntries.map(t => (
              <tr 
                key={t.timeEntryId} 
                onClick={() => handleRowClick(t)}
                style={{ 
                  borderBottom: '1px solid #e0e0e0',
                  backgroundColor: t.billed ? '#f5f5f5' : 'transparent',
                  cursor: getTimeEntryCursorStyle(t)
                }}
                data-testid={`time-entry-row-${t.timeEntryId}`}
                data-billed={t.billed}
                data-clickable={isTimeEntryClickable(t)}
              >
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  {t.billed && (
                    <span 
                      data-testid={`billed-icon-${t.timeEntryId}`}
                      title="Billed"
                      style={{ 
                        color: '#4caf50', 
                        fontWeight: 'bold',
                        fontSize: '14px'
                      }}
                    >
                      $
                    </span>
                  )}
                </td>
                <td style={{ padding: '8px' }}>{formatLocalDate(t.date)}</td>
                <td style={{ padding: '8px', textTransform: 'capitalize' }}>{t.activityType.replace('-', ' ')}</td>
                <td style={{ padding: '8px' }}>{t.description}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{Math.floor(t.duration / 60)}h {t.duration % 60}m</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>{t.billable ? '✓' : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// Task Section Component
const TaskSection: React.FC<{ caseId: string; tasks: Task[]; showForm: boolean; setShowForm: (v: boolean) => void; onRefresh: () => void }> = ({ caseId, tasks, showForm, setShowForm, onRefresh }) => {
  const [form, setForm] = useState<CreateTaskRequest>({ caseId, title: '', description: '', priority: 'medium' });
  const [saving, setSaving] = useState(false);
  // Store the task being completed - task is NOT updated until user confirms (Skip or Log Time)
  const [completingTask, setCompletingTask] = useState<Task | null>(null);
  const [timeForm, setTimeForm] = useState<CreateTimeEntryRequest>({ caseId, date: new Date().toISOString().split('T')[0], duration: 15, activityType: 'other', description: '', billable: true });
  const [savingTime, setSavingTime] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      await tasksApi.create(form);
      setShowForm(false);
      setForm({ ...form, title: '', description: '' });
      onRefresh();
    } catch (err) {
      alert('Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  // Refactored: Only show modal, don't update task immediately
  // Task update is deferred until user clicks Skip or Log Time
  const toggleStatus = async (task: Task) => {
    if (task.status === 'completed') {
      // If unchecking a completed task, update immediately
      await tasksApi.update(task.taskId, { status: 'pending' });
      onRefresh();
    } else {
      // If marking as complete, show time entry prompt first (don't update task yet)
      setCompletingTask(task);
      setTimeForm({ ...timeForm, description: `Completed: ${task.title}` });
    }
  };

  // Updated: Now also updates task status to completed
  const handleTimeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!completingTask) return;
    try {
      setSavingTime(true);
      // First update task status to completed
      await tasksApi.update(completingTask.taskId, { status: 'completed' });
      // Then create time entry
      await timeApi.create(timeForm);
      setCompletingTask(null);
      setTimeForm({ ...timeForm, description: '' });
      onRefresh();
    } catch (err) {
      alert('Failed to save time entry');
    } finally {
      setSavingTime(false);
    }
  };

  // Updated: Now also updates task status to completed
  const skipTimeEntry = async () => {
    if (!completingTask) return;
    try {
      setSavingTime(true);
      // Update task status to completed
      await tasksApi.update(completingTask.taskId, { status: 'completed' });
      setCompletingTask(null);
      onRefresh();
    } catch (err) {
      alert('Failed to complete task');
    } finally {
      setSavingTime(false);
    }
  };

  // Cancel: Close modal without making any API calls, task remains unchanged
  const cancelTaskCompletion = () => {
    setCompletingTask(null);
    setTimeForm({ ...timeForm, description: '' });
  };

  const priorityColors: Record<TaskPriority, string> = { low: '#9e9e9e', medium: '#ff9800', high: '#f44336' };

  return (
    <div>
      {/* Time Entry Prompt Modal */}
      {completingTask && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} data-testid="task-time-entry-modal">
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '500px', width: '90%' }}>
            <h3 style={{ marginTop: 0 }}>Log Time for Completed Task?</h3>
            <p style={{ color: '#666', marginBottom: '16px' }}>Complete task "{completingTask.title}"? Would you like to log time spent?</p>
            <form onSubmit={handleTimeSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Date</label>
                  <input type="date" value={timeForm.date} onChange={e => setTimeForm({ ...timeForm, date: e.target.value })} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} required data-testid="task-time-date-input" />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Duration</label>
                  <select value={timeForm.duration} onChange={e => setTimeForm({ ...timeForm, duration: Number(e.target.value) })} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} data-testid="task-time-duration-select">
                    {[15, 30, 45, 60, 90, 120, 180, 240].map(d => <option key={d} value={d}>{d} min</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Activity Type</label>
                <select value={timeForm.activityType} onChange={e => setTimeForm({ ...timeForm, activityType: e.target.value as ActivityType })} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} data-testid="task-time-activity-select">
                  {ACTIVITY_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Description</label>
                <input value={timeForm.description} onChange={e => setTimeForm({ ...timeForm, description: e.target.value })} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} required data-testid="task-time-description-input" />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <input type="checkbox" checked={timeForm.billable} onChange={e => setTimeForm({ ...timeForm, billable: e.target.checked })} id="billable" data-testid="task-time-billable-checkbox" />
                <label htmlFor="billable">Billable</label>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={cancelTaskCompletion} disabled={savingTime} style={{ padding: '8px 16px', border: '1px solid #9e9e9e', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'white', color: '#666' }} data-testid="task-time-cancel-button">
                  Cancel
                </button>
                <button type="button" onClick={skipTimeEntry} disabled={savingTime} style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'white' }} data-testid="task-time-skip-button">
                  {savingTime ? 'Completing...' : 'Skip'}
                </button>
                <button type="submit" disabled={savingTime} style={{ backgroundColor: '#1976d2', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }} data-testid="task-time-submit-button">
                  {savingTime ? 'Saving...' : 'Log Time'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0 }}>Tasks</h3>
        <button onClick={() => setShowForm(!showForm)} style={{ backgroundColor: '#4caf50', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>
          {showForm ? 'Cancel' : '+ Add Task'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Title</label>
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Task title" style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} required />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Due Date</label>
              <input type="date" value={form.dueDate || ''} onChange={e => setForm({ ...form, dueDate: e.target.value || undefined })} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value as TaskPriority })} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>
          <button type="submit" disabled={saving} style={{ backgroundColor: '#1976d2', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>
            {saving ? 'Saving...' : 'Save Task'}
          </button>
        </form>
      )}

      {tasks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px', color: '#666', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>No tasks yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {tasks.map(t => (
            <div key={t.taskId} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: t.status === 'completed' ? '#f5f5f5' : 'white', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
              <input type="checkbox" checked={t.status === 'completed'} onChange={() => toggleStatus(t)} style={{ width: '20px', height: '20px', cursor: 'pointer' }} />
              <div style={{ flex: 1, textDecoration: t.status === 'completed' ? 'line-through' : 'none', color: t.status === 'completed' ? '#999' : '#333' }}>
                <div style={{ fontWeight: 500 }}>{t.title}</div>
                {t.dueDate && <div style={{ fontSize: '12px', color: '#666' }}>Due: {formatLocalDate(t.dueDate)}</div>}
              </div>
              <span style={{ backgroundColor: priorityColors[t.priority], color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', textTransform: 'uppercase' }}>{t.priority}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


const DOCUMENT_CATEGORIES: { value: DocumentCategory; label: string }[] = [
  { value: 'iep', label: 'IEP' },
  { value: 'evaluation', label: 'Evaluation' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'medical', label: 'Medical' },
  { value: 'legal', label: 'Legal' },
  { value: 'other', label: 'Other' },
];

// V2 Document Categories for filtering
const DOCUMENT_CATEGORIES_V2: { value: DocumentCategoryV2 | 'all'; label: string }[] = [
  { value: 'all', label: 'All Categories' },
  { value: 'IEP', label: 'IEP' },
  { value: 'EVAL_REPORT', label: 'Evaluation Report' },
  { value: 'PRIOR_WRITTEN_NOTICE', label: 'Prior Written Notice' },
  { value: 'MEETING_NOTICE', label: 'Meeting Notice' },
  { value: 'PROGRESS_REPORT', label: 'Progress Report' },
  { value: 'CORRESPONDENCE', label: 'Correspondence' },
  { value: 'MEDICAL_RECORD', label: 'Medical Record' },
  { value: 'SCHOOL_RECORD', label: 'School Record' },
  { value: 'LEGAL_DOCUMENT', label: 'Legal Document' },
  { value: 'EMAIL_MESSAGE', label: 'Email Message' },
  { value: 'OTHER', label: 'Other' },
];

const PROCESSING_STATUSES: { value: DocumentProcessingStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'complete', label: 'Complete' },
  { value: 'failed', label: 'Failed' },
];

// Document Section V2 Component with status badges, upload modal, and filters
const DocumentSectionV2: React.FC<{ caseId: string; documents: DocumentV2[]; onRefresh: () => void }> = ({ caseId, documents, onRefresh }) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<DocumentCategoryV2 | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<DocumentProcessingStatus | 'all'>('all');

  const handleUploadComplete = (documentIds: string[], assemblyId?: string) => {
    setShowUploadModal(false);
    onRefresh();
  };

  const handleDownload = async (doc: DocumentV2) => {
    try {
      const { downloadUrl } = await documentsApi.getDownloadUrl(doc.documentId);
      window.open(downloadUrl, '_blank');
    } catch (err) {
      alert('Failed to download document');
    }
  };

  const handleDelete = async (doc: DocumentV2) => {
    if (!window.confirm(`Delete "${doc.fileName}"?`)) return;
    try {
      await documentsApi.delete(doc.documentId);
      onRefresh();
    } catch (err) {
      alert('Failed to delete document');
    }
  };

  const handleReprocess = async (doc: DocumentV2) => {
    try {
      await documentsApi.reprocessDocument(doc.documentId);
      onRefresh();
    } catch (err) {
      alert('Failed to reprocess document');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusBadgeStyle = (status: DocumentProcessingStatus): React.CSSProperties => {
    const colors: Record<DocumentProcessingStatus, { bg: string; text: string }> = {
      pending: { bg: '#fff3e0', text: '#e65100' },
      processing: { bg: '#e3f2fd', text: '#1565c0' },
      complete: { bg: '#e8f5e9', text: '#2e7d32' },
      failed: { bg: '#ffebee', text: '#c62828' },
    };
    const { bg, text } = colors[status] || { bg: '#f5f5f5', text: '#666' };
    return {
      backgroundColor: bg,
      color: text,
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 500,
      textTransform: 'capitalize',
    };
  };

  const getCategoryLabel = (category: string): string => {
    const found = DOCUMENT_CATEGORIES_V2.find(c => c.value === category);
    return found?.label || category;
  };

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    if (categoryFilter !== 'all' && doc.category !== categoryFilter && doc.advocateCategory !== categoryFilter) {
      return false;
    }
    if (statusFilter !== 'all' && doc.status !== statusFilter) {
      return false;
    }
    return true;
  });

  return (
    <div>
      {/* Upload Modal */}
      {showUploadModal && (
        <DocumentUploadModal
          caseId={caseId}
          onUploadComplete={handleUploadComplete}
          onClose={() => setShowUploadModal(false)}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0 }}>Documents</h3>
        <button
          onClick={() => setShowUploadModal(true)}
          style={{ backgroundColor: '#4caf50', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
          data-testid="upload-documents-button"
        >
          + Upload Documents
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as DocumentCategoryV2 | 'all')}
          style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          data-testid="category-filter"
        >
          {DOCUMENT_CATEGORIES_V2.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as DocumentProcessingStatus | 'all')}
          style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          data-testid="status-filter"
        >
          {PROCESSING_STATUSES.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        {(categoryFilter !== 'all' || statusFilter !== 'all') && (
          <button
            onClick={() => { setCategoryFilter('all'); setStatusFilter('all'); }}
            style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', fontSize: '14px' }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Document List */}
      {filteredDocuments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px', color: '#666', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          {documents.length === 0 ? 'No documents yet. Upload your first document!' : 'No documents match the selected filters.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filteredDocuments.map(doc => (
            <div key={doc.documentId} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e0e0e0' }} data-testid={`document-row-${doc.documentId}`}>
              <div style={{ width: '40px', height: '40px', backgroundColor: '#e3f2fd', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#1976d2', fontWeight: 500 }}>
                {doc.fileName.split('.').pop()?.toUpperCase().slice(0, 4)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {doc.fileName}
                  <span style={getStatusBadgeStyle(doc.status)} data-testid={`status-badge-${doc.documentId}`}>
                    {doc.status}
                  </span>
                  {doc.isSuperseded && (
                    <span style={{ backgroundColor: '#f5f5f5', color: '#666', padding: '2px 8px', borderRadius: '12px', fontSize: '11px' }}>
                      Superseded
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {getCategoryLabel(doc.advocateCategory || doc.category)} • {formatFileSize(doc.fileSize)} • {formatLocalDate(doc.uploadedAt)}
                  {doc.source === 'email' && ' • via Email'}
                </div>
                {doc.status === 'failed' && doc.processingError && (
                  <div style={{ fontSize: '12px', color: '#c62828', marginTop: '4px' }}>
                    Error: {doc.processingError}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {doc.status === 'failed' && (
                  <button onClick={() => handleReprocess(doc)} style={{ backgroundColor: '#ff9800', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }} data-testid={`reprocess-${doc.documentId}`}>
                    Retry
                  </button>
                )}
                <button onClick={() => handleDownload(doc)} style={{ backgroundColor: '#4caf50', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                  Download
                </button>
                <button onClick={() => handleDelete(doc)} style={{ backgroundColor: '#f44336', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Package Section Component
const PackageSection: React.FC<{ caseId: string; packages: DocumentPackage[]; onRefresh: () => void }> = ({ caseId, packages, onRefresh }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);

  const handlePackageCreated = (pkg: DocumentPackage) => {
    setShowCreateModal(false);
    onRefresh();
  };

  const handleSelectPackage = (pkg: DocumentPackage) => {
    setSelectedPackageId(pkg.packageId);
  };

  const handleBack = () => {
    setSelectedPackageId(null);
  };

  const handleRemoveDocument = async (documentId: string) => {
    if (!selectedPackageId) return;
    await packagesApi.removeDocument(selectedPackageId, documentId);
  };

  const handleDeletePackage = async () => {
    if (!selectedPackageId) return;
    await packagesApi.delete(selectedPackageId);
    setSelectedPackageId(null);
    onRefresh();
  };

  const handleDocumentSelect = (doc: DocumentV2) => {
    // Could navigate to document detail view in the future
    console.log('Selected document:', doc.documentId);
  };

  if (selectedPackageId) {
    return (
      <PackageDetail
        packageId={selectedPackageId}
        onBack={handleBack}
        onDocumentSelect={handleDocumentSelect}
        onRemoveDocument={handleRemoveDocument}
        onDeletePackage={handleDeletePackage}
      />
    );
  }

  return (
    <div>
      {showCreateModal && (
        <CreatePackageModal
          caseId={caseId}
          onCreated={handlePackageCreated}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      <PackageList
        caseId={caseId}
        onSelectPackage={handleSelectPackage}
        onCreatePackage={() => setShowCreateModal(true)}
      />
    </div>
  );
};

// Legacy Document Section Component (kept for backwards compatibility)
const DocumentSection: React.FC<{ caseId: string; documents: Document[]; onRefresh: () => void }> = ({ caseId, documents, onRefresh }) => {
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState<DocumentCategory>('other');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    const file = files[0];
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      alert('File size exceeds 50MB limit');
      return;
    }

    try {
      setUploading(true);
      await documentsApi.upload(caseId, file, category);
      onRefresh();
    } catch (err) {
      alert('Failed to upload document');
      console.error(err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      const { downloadUrl } = await documentsApi.getDownloadUrl(doc.documentId);
      window.open(downloadUrl, '_blank');
    } catch (err) {
      alert('Failed to download document');
    }
  };

  const handleDelete = async (doc: Document) => {
    if (!window.confirm(`Delete "${doc.fileName}"?`)) return;
    try {
      await documentsApi.delete(doc.documentId);
      onRefresh();
    } catch (err) {
      alert('Failed to delete document');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getCategoryLabel = (cat: DocumentCategory) => DOCUMENT_CATEGORIES.find(c => c.value === cat)?.label || cat;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0 }}>Documents</h3>
      </div>

      {/* Upload Area */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files); }}
        style={{
          border: `2px dashed ${dragOver ? '#1976d2' : '#ddd'}`,
          borderRadius: '8px',
          padding: '24px',
          textAlign: 'center',
          marginBottom: '16px',
          backgroundColor: dragOver ? '#e3f2fd' : '#f5f5f5',
          transition: 'all 0.2s',
        }}
      >
        <div style={{ marginBottom: '12px', color: '#666' }}>
          {uploading ? 'Uploading...' : 'Drag & drop a file here, or click to select'}
        </div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center' }}>
          <select value={category} onChange={e => setCategory(e.target.value as DocumentCategory)} style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}>
            {DOCUMENT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <input
            ref={fileInputRef}
            type="file"
            onChange={e => handleUpload(e.target.files)}
            style={{ display: 'none' }}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{ backgroundColor: '#1976d2', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
          >
            {uploading ? 'Uploading...' : 'Select File'}
          </button>
        </div>
        <div style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
          Max 50MB. Allowed: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG
        </div>
      </div>

      {/* Document List */}
      {documents.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px', color: '#666', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>No documents yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {documents.map(doc => (
            <div key={doc.documentId} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
              <div style={{ width: '40px', height: '40px', backgroundColor: '#e3f2fd', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#1976d2', fontWeight: 500 }}>
                {doc.fileName.split('.').pop()?.toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{doc.fileName}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {getCategoryLabel(doc.category)} • {formatFileSize(doc.fileSize)} • {new Date(doc.uploadedAt).toLocaleDateString()}
                </div>
              </div>
              <button onClick={() => handleDownload(doc)} style={{ backgroundColor: '#4caf50', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                Download
              </button>
              <button onClick={() => handleDelete(doc)} style={{ backgroundColor: '#f44336', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Invoice Section Component
const InvoiceSection: React.FC<{ caseId: string; invoices: Invoice[]; timeEntries: TimeEntry[]; onRefresh: () => void }> = ({ caseId, invoices, timeEntries, onRefresh }) => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [selectedEntries, setSelectedEntries] = useState<string[]>([]);
  const [rate, setRate] = useState(15000); // $150/hr in cents
  const [creating, setCreating] = useState(false);
  const [sendingInvoiceId, setSendingInvoiceId] = useState<string | null>(null);
  const [showSendConfirm, setShowSendConfirm] = useState<Invoice | null>(null);

  const unbilledEntries = timeEntries.filter(t => t.billable && !t.billed);

  const handleCreate = async () => {
    if (selectedEntries.length === 0) {
      alert('Please select at least one time entry');
      return;
    }
    try {
      setCreating(true);
      await invoicesApi.create({ caseId, timeEntryIds: selectedEntries, rate });
      setShowForm(false);
      setSelectedEntries([]);
      onRefresh();
    } catch (err) {
      alert('Failed to create invoice');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (invoice: Invoice, newStatus: InvoiceStatus) => {
    try {
      await invoicesApi.update(invoice.invoiceId, { status: newStatus });
      onRefresh();
    } catch (err) {
      alert('Failed to update invoice status');
    }
  };

  const handleDelete = async (invoice: Invoice) => {
    if (!window.confirm(`Delete invoice ${invoice.invoiceNumber}?`)) return;
    try {
      await invoicesApi.delete(invoice.invoiceId);
      onRefresh();
    } catch (err) {
      alert('Failed to delete invoice');
    }
  };

  const handleSend = async (invoice: Invoice) => {
    try {
      setSendingInvoiceId(invoice.invoiceId);
      setShowSendConfirm(null);
      const result = await invoicesApi.send(invoice.invoiceId);
      alert(`Invoice sent successfully to ${result.recipientEmail}`);
      onRefresh();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send invoice';
      alert(errorMessage);
    } finally {
      setSendingInvoiceId(null);
    }
  };

  const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  const canSendInvoice = (invoice: Invoice) => {
    return invoice.status !== 'draft' && invoice.status !== 'cancelled';
  };

  const statusColors: Record<InvoiceStatus, string> = {
    draft: '#9e9e9e',
    sent: '#2196f3',
    paid: '#4caf50',
    overdue: '#f44336',
    cancelled: '#757575',
  };

  const selectedTotal = selectedEntries.reduce((sum, id) => {
    const entry = unbilledEntries.find(e => e.timeEntryId === id);
    return sum + (entry ? Math.round((entry.duration / 60) * rate) : 0);
  }, 0);

  return (
    <div>
      {/* Send Confirmation Modal */}
      {showSendConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '400px', width: '90%' }}>
            <h3 style={{ marginTop: 0 }}>Send Invoice?</h3>
            <p style={{ color: '#666', marginBottom: '16px' }}>
              This will email invoice {showSendConfirm.invoiceNumber} to the client. Are you sure you want to proceed?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowSendConfirm(null)} style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'white' }}>
                Cancel
              </button>
              <button onClick={() => handleSend(showSendConfirm)} style={{ backgroundColor: '#1976d2', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>
                Send Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0 }}>Invoices</h3>
        <button onClick={() => setShowForm(!showForm)} disabled={unbilledEntries.length === 0} style={{ backgroundColor: unbilledEntries.length === 0 ? '#ccc' : '#4caf50', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: unbilledEntries.length === 0 ? 'not-allowed' : 'pointer' }}>
          {showForm ? 'Cancel' : '+ Create Invoice'}
        </button>
      </div>

      {showForm && (
        <div style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
          <h4 style={{ marginTop: 0 }}>Select Time Entries to Invoice</h4>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Hourly Rate</label>
            <input type="number" value={rate / 100} onChange={e => setRate(Math.round(Number(e.target.value) * 100))} style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px', width: '120px' }} min="0" step="0.01" />
            <span style={{ marginLeft: '8px', color: '#666' }}>/hour</span>
          </div>
          <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '12px' }}>
            {unbilledEntries.map(entry => (
              <label key={entry.timeEntryId} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', backgroundColor: 'white', borderRadius: '4px', marginBottom: '4px', cursor: 'pointer' }}>
                <input type="checkbox" checked={selectedEntries.includes(entry.timeEntryId)} onChange={e => {
                  if (e.target.checked) setSelectedEntries([...selectedEntries, entry.timeEntryId]);
                  else setSelectedEntries(selectedEntries.filter(id => id !== entry.timeEntryId));
                }} />
                <span style={{ flex: 1 }}>{formatLocalDate(entry.date)} - {entry.description}</span>
                <span style={{ color: '#666' }}>{entry.duration}m</span>
                <span style={{ fontWeight: 500 }}>{formatCurrency(Math.round((entry.duration / 60) * rate))}</span>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontWeight: 500 }}>Total: {formatCurrency(selectedTotal)}</div>
            <button onClick={handleCreate} disabled={creating || selectedEntries.length === 0} style={{ backgroundColor: '#1976d2', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>
              {creating ? 'Creating...' : 'Create Invoice'}
            </button>
          </div>
        </div>
      )}

      {invoices.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px', color: '#666', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          {unbilledEntries.length > 0 ? `${unbilledEntries.length} unbilled time entries available` : 'No invoices yet.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {invoices.map(inv => (
            <div key={inv.invoiceId} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
              <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => navigate(`/invoices/${inv.invoiceId}`)}>
                <div style={{ fontWeight: 500, color: '#1976d2' }}>{inv.invoiceNumber}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {formatLocalDate(inv.issueDate)} • {inv.lineItems.length} items • Due: {formatLocalDate(inv.dueDate)}
                  {inv.sentAt && <span> • Sent: {formatDateTime(inv.sentAt)}</span>}
                </div>
              </div>
              <div style={{ fontWeight: 500, fontSize: '16px' }}>{formatCurrency(inv.total)}</div>
              <span style={{ backgroundColor: statusColors[inv.status], color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', textTransform: 'uppercase' }}>{inv.status}</span>
              <button onClick={() => navigate(`/invoices/${inv.invoiceId}`)} style={{ backgroundColor: '#f5f5f5', color: '#333', border: '1px solid #ddd', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                View
              </button>
              {inv.status === 'draft' && (
                <>
                  <button onClick={() => setShowSendConfirm(inv)} disabled={sendingInvoiceId === inv.invoiceId} style={{ backgroundColor: '#1976d2', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                    {sendingInvoiceId === inv.invoiceId ? 'Sending...' : 'Send Invoice'}
                  </button>
                  <button onClick={() => handleDelete(inv)} style={{ backgroundColor: '#f44336', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                    Delete
                  </button>
                </>
              )}
              {canSendInvoice(inv) && inv.status !== 'draft' && (
                <button onClick={() => setShowSendConfirm(inv)} disabled={sendingInvoiceId === inv.invoiceId} style={{ backgroundColor: '#1976d2', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                  {sendingInvoiceId === inv.invoiceId ? 'Sending...' : 'Resend'}
                </button>
              )}
              {inv.status === 'sent' && (
                <button onClick={() => handleStatusChange(inv, 'paid')} style={{ backgroundColor: '#4caf50', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                  Mark Paid
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
